import { useEffect, useMemo, useRef, useState } from 'react';
import { Search, X } from 'lucide-react';
import { getHotels, getHotelCountries } from '../../../../../api/products/useHotelsApi';
import styles from '../HomepageSettings.module.css';

// ISO alpha-2 → flag emoji, so a picked hotel reads "🇹🇷 Antalya, Turkey" on the
// homepage card exactly like the hand-written entries did.
const flagEmoji = (iso) => {
  const cc = String(iso || '').trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(cc)) return '';
  const BASE = 0x1f1e6;
  return (
    String.fromCodePoint(BASE + cc.charCodeAt(0) - 65) +
    String.fromCodePoint(BASE + cc.charCodeAt(1) - 65)
  );
};

// Country names are looked up once and cached across every picker on the page.
let countryNamePromise = null;
const loadCountryNames = () => {
  if (!countryNamePromise) {
    countryNamePromise = getHotelCountries()
      .then((res) => {
        const map = {};
        for (const c of res?.data || []) {
          if (c?.countryCode) map[c.countryCode] = c.countryName || c.countryCode;
        }
        return map;
      })
      .catch(() => ({}));
  }
  return countryNamePromise;
};

export const buildHotelLocation = (hotel, countryNames = {}) => {
  const code = hotel?.countryCode || '';
  const country = countryNames[code] || code;
  const parts = [hotel?.city, country].filter(Boolean).join(', ');
  const flag = flagEmoji(code);
  return [flag, parts].filter(Boolean).join(' ').trim();
};

/**
 * Search the hotel database and pick one. onPick receives the full hotel row so
 * the caller can fill name / location / stars / image from real data.
 */
export default function HotelPicker({ onPick, disabled }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [countryNames, setCountryNames] = useState({});
  const boxRef = useRef(null);

  useEffect(() => {
    loadCountryNames().then(setCountryNames);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Debounced search — the hotels table is large, so only query on real input.
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setResults([]);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(() => {
      getHotels({ search: q, limit: 20, status: 'active' })
        .then((res) => {
          if (cancelled) return;
          setResults(Array.isArray(res?.data) ? res.data : []);
          setOpen(true);
        })
        .catch(() => { if (!cancelled) setResults([]); })
        .finally(() => { if (!cancelled) setLoading(false); });
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [query]);

  const hint = useMemo(() => {
    if (loading) return 'Searching…';
    if (query.trim().length > 0 && query.trim().length < 2) return 'Type at least 2 characters';
    if (query.trim().length >= 2 && results.length === 0 && !loading) return 'No hotels found';
    return '';
  }, [loading, query, results.length]);

  const choose = (hotel) => {
    onPick(hotel, buildHotelLocation(hotel, countryNames));
    setQuery('');
    setResults([]);
    setOpen(false);
  };

  return (
    <div className={styles.hotelPicker} ref={boxRef}>
      <div className={styles.hotelPickerInput}>
        <Search size={14} />
        <input
          value={query}
          disabled={disabled}
          placeholder="Search hotels in the database…"
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => results.length && setOpen(true)}
        />
        {query && (
          <button type="button" className={styles.hotelPickerClear} onClick={() => { setQuery(''); setResults([]); }}>
            <X size={13} />
          </button>
        )}
      </div>
      {hint && <span className={styles.fieldHint}>{hint}</span>}
      {open && results.length > 0 && (
        <div className={styles.hotelPickerDropdown}>
          {results.map((h) => (
            <button key={h.id} type="button" className={styles.hotelPickerOption} onClick={() => choose(h)}>
              {h.primaryImage ? (
                <img src={h.primaryImage} alt="" className={styles.hotelPickerThumb} />
              ) : (
                <span className={styles.hotelPickerThumbEmpty} />
              )}
              <span className={styles.hotelPickerText}>
                <span className={styles.hotelPickerName}>{h.name}</span>
                <span className={styles.hotelPickerMeta}>
                  {buildHotelLocation(h, countryNames) || '—'}
                  {h.stars ? ` · ${h.stars}★` : ''}
                </span>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
